import React, { useEffect, useRef, useState } from 'react';
import { authApi } from '../services/api';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import LegalLinks from '../components/legal/LegalLinks';
import { useAuth } from '../contexts/AuthContext';
import BrandLogo from '../components/BrandLogo';

// ---- Step indicator ----
const StepBar: React.FC<{ currentStep: number }> = ({ currentStep }) => (
  <div
    className="bg-white rounded-[24px] px-5 py-4 flex items-center gap-0"
    style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
  >
    <BrandLogo variant="mark" className="h-[42px] object-contain mr-6" />
    <div className="flex items-center flex-1 justify-end">
      {[1, 2, 3, 4, 5].map((step, i) => (
        <React.Fragment key={step}>
          {i > 0 && (
            <div
              className="h-[6px] flex-1 max-w-[94px] rounded-full"
              style={{ background: i < currentStep ? '#3973bf' : '#c5c5c5' }}
            />
          )}
          <div
            className="w-[40px] h-[40px] rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: step <= currentStep ? '#3973bf' : '#d9d9d9' }}
          >
            <span style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 700, color: '#fafafa' }}>{step}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>
);

// ---- Input component ----
const FormInput: React.FC<{
  label: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  icon: React.ReactNode;
  type?: string;
  prefix?: string;
  /** Hard cap enforced by the browser, matching the server's column limits. */
  maxLength?: number;
  /** For type="date": the latest selectable day (birth dates can't be future). */
  max?: string;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
}> = ({ label, placeholder, value, onChange, icon, type = 'text', prefix, maxLength, max, inputMode }) => (
  <div className="mb-5">
    <label style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 500, color: '#2b2b2b', display: 'block', marginBottom: 8 }}>
      {label}
    </label>
    <div
      className="flex items-center bg-[#fafafa] rounded-[20px] h-[80px] px-6 gap-4"
      style={{ border: '2px solid #3973bf' }}
    >
      <div className="flex-shrink-0 w-[32px] flex items-center justify-center">{icon}</div>
      {prefix && (
        <span style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 600, color: '#000' }}>{prefix}</span>
      )}
      <PasswordAwareInput type={type} value={value} onChange={onChange} placeholder={placeholder || label}
        maxLength={maxLength} max={max} inputMode={inputMode} />
    </div>
  </div>
);

// ---- Custom select (AP32) ----
// Native <select> yerine tamamen stillenebilir dropdown: kapalı hâli FormInput
// kutusuyla birebir aynı, ok ikonu stroke'lu chevron (eski fill'li ok blob gibi
// görünüyordu), açılan panel uygulama stilinde. `onSearchChange` verilirse arama
// sunucu-modludur (parent listeyi getirir); verilmezse `searchable` yerel filtreler.
type SelectOption = { id: number | string; name: string };

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="20" height="12" viewBox="0 0 20 12" fill="none" stroke="#2b2b2b"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    className={`flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
  >
    <path d="M2 2l8 8 8-8" />
  </svg>
);

const FormSelect: React.FC<{
  label: string;
  placeholder: string;
  value: string | null;
  options: SelectOption[];
  onSelect: (o: SelectOption) => void;
  icon: React.ReactNode;
  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (q: string) => void;
  loading?: boolean;
  disabled?: boolean;
  disabledText?: string;
  footerNote?: string | null;
  emptyText?: string;
}> = ({ label, placeholder, value, options, onSelect, icon, searchable, searchValue, onSearchChange, loading, disabled, disabledText, footerNote, emptyText }) => {
  const [open, setOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const serverMode = !!onSearchChange;
  const query = serverMode ? (searchValue ?? '') : localQuery;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const shown = serverMode || !searchable || !query
    ? options
    : options.filter((o) => o.name.toLocaleLowerCase('tr').includes(query.toLocaleLowerCase('tr')));

  return (
    <div className="mb-5" ref={wrapRef}>
      <label style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 500, color: '#2b2b2b', display: 'block', marginBottom: 8 }}>
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className={`w-full flex items-center bg-[#fafafa] rounded-[20px] h-[80px] px-6 gap-4 text-left ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          style={{ border: '2px solid #3973bf' }}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <div className="flex-shrink-0 w-[32px] flex items-center justify-center">{icon}</div>
          <span className="flex-1 truncate text-[18px] font-medium" style={{ fontFamily: 'Inter', color: value ? '#000' : '#2b2b2b' }}>
            {disabled && disabledText ? disabledText : (value || placeholder)}
          </span>
          <Chevron open={open} />
        </button>

        {open && !disabled && (
          <div
            className="absolute left-0 right-0 top-full mt-2 z-40 bg-white rounded-[20px] overflow-hidden"
            style={{ border: '2px solid #3973bf', boxShadow: '0px 8px 24px rgba(0,0,0,0.18)' }}
            role="listbox"
          >
            {searchable && (
              <div className="p-3" style={{ borderBottom: '1.5px solid #e3e9f4' }}>
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => (serverMode ? onSearchChange!(e.target.value) : setLocalQuery(e.target.value))}
                  placeholder="Ara…"
                  className="w-full h-[48px] rounded-[14px] px-4 text-[16px] font-medium outline-none bg-[#f2f6fd] placeholder:text-[#8d8d8d]"
                  style={{ fontFamily: 'Inter', border: '1.5px solid #c9d8f0' }}
                />
              </div>
            )}
            <div className="max-h-[264px] overflow-y-auto">
              {loading ? (
                <p className="px-6 py-4 text-[15px] text-[#8d8d8d]" style={{ fontFamily: 'Inter' }}>Yükleniyor…</p>
              ) : shown.length === 0 ? (
                <p className="px-6 py-4 text-[15px] text-[#8d8d8d]" style={{ fontFamily: 'Inter' }}>{emptyText ?? 'Sonuç bulunamadı'}</p>
              ) : (
                shown.map((o) => {
                  const selected = value === o.name;
                  return (
                    <button
                      type="button"
                      key={String(o.id)}
                      role="option"
                      aria-selected={selected}
                      onClick={() => {
                        onSelect(o);
                        setOpen(false);
                        if (!serverMode) setLocalQuery('');
                      }}
                      className={`w-full text-left px-6 py-3.5 text-[17px] transition-colors ${selected ? 'bg-[#eaf1fb] font-semibold text-[#3973bf]' : 'text-[#2b2b2b] hover:bg-[#f2f6fd] font-medium'}`}
                      style={{ fontFamily: 'Inter' }}
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span className="truncate">{o.name}</span>
                        {selected && (
                          <svg width="18" height="14" viewBox="0 0 18 14" fill="none" stroke="#3973bf" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                            <path d="M2 7.5L6.5 12L16 2" />
                          </svg>
                        )}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
            {footerNote && (
              <p className="px-6 py-2.5 text-[13px] text-[#8d8d8d]" style={{ fontFamily: 'Inter', borderTop: '1.5px solid #e3e9f4' }}>{footerNote}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Shows a show/hide eye toggle when type='password'. Otherwise plain input.
const PasswordAwareInput: React.FC<{
  type: string; value: string; onChange: (v: string) => void; placeholder: string;
  maxLength?: number; max?: string; inputMode?: 'text' | 'numeric' | 'tel' | 'email';
}> = ({ type, value, onChange, placeholder, maxLength, max, inputMode }) => {
  const isPassword = type === 'password';
  const [show, setShow] = React.useState(false);
  const effectiveType = isPassword ? (show ? 'text' : 'password') : type;
  return (
    <>
      <input
        type={effectiveType}
        value={value}
        onChange={(e) => onChange(isPassword ? e.target.value : (type === 'email' ? e.target.value.trim() : e.target.value))}
        placeholder={placeholder}
        maxLength={maxLength}
        max={max}
        inputMode={inputMode}
        autoCapitalize={isPassword || type === 'email' ? 'off' : undefined}
        autoCorrect={isPassword || type === 'email' ? 'off' : undefined}
        spellCheck={isPassword || type === 'email' ? false : undefined}
        className="flex-1 bg-transparent outline-none text-[18px] font-medium text-[#000] placeholder:text-[#2b2b2b]"
        style={{ fontFamily: 'Inter' }}
      />
      {isPassword && (
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="text-[#3973bf] hover:text-[#2b58a0] focus:outline-none flex-shrink-0"
          aria-label={show ? 'Şifreyi gizle' : 'Şifreyi göster'}
        >
          {show ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a21.84 21.84 0 015.17-6.17M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a21.77 21.77 0 01-3.17 4.35M1 1l22 22" />
              <path d="M9.88 9.88a3 3 0 104.24 4.24" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      )}
    </>
  );
};

// ---- Icons ----
const MailIcon = () => (
  <svg width="32" height="26" viewBox="0 0 32 26" fill="none" stroke="#3973bf" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1.5" y="1.5" width="29" height="23" rx="4" /><path d="M2 2l14 11L30 2" />
  </svg>
);
const KeyIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.78 7.78 5.5 5.5 0 017.78-7.78zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);
const PhoneIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#3973bf"><path d="M6.6 10.8a15.3 15.3 0 006.6 6.6l2.2-2.2a1 1 0 011-.2 11.4 11.4 0 003.6.6 1 1 0 011 1v3.4a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.4 11.4 0 00.6 3.6 1 1 0 01-.3 1L6.6 10.8z" /></svg>
);
const PersonIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#3973bf"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z" /></svg>
);
const CalendarIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="3" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
);
const LocationIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#3973bf"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z" /></svg>
);
const SchoolIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#3973bf"><path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z" /></svg>
);

const ROLE_MAP: Record<string, 'student' | 'teacher' | 'parent'> = {
  'Öğrenci': 'student',
  'Öğretmen': 'teacher',
  'Veli': 'parent',
};

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  // AP33: adım artık URL'de (?adim=N). Böylece tarayıcı/telefon geri tuşu VE
  // üst-soldaki "Geri" oku bir önceki adıma döner; adım 1'de login'e çıkılır.
  const [searchParams, setSearchParams] = useSearchParams();
  const step = Math.min(Math.max(parseInt(searchParams.get('adim') ?? '1', 10) || 1, 1), 6);
  const goToStep = (n: number) => setSearchParams(n <= 1 ? {} : { adim: String(n) });
  // Yenileme / derin link ile ortadan başlama: form state boş olacağından
  // 1. adıma resetle (replace — history kirlenmesin).
  useEffect(() => {
    if (parseInt(searchParams.get('adim') ?? '1', 10) > 1) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [showCodeInfo, setShowCodeInfo] = useState(false);
  const [error, setError] = useState('');

  // Step 1
  const [regType, setRegType] = useState('Öğrenci');
  const [publisherCode, setPublisherCode] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Step 2
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState('');

  // Step 3 — AP34: Şehir gerçek katalogdan gelir (82 il), Okul listesi şehirle
  // senkron ve sunucu aramalı (50k kurumdan ilk 50'si + arama daraltması).
  const [city, setCity] = useState<{ id: number; name: string } | null>(null);
  const [school, setSchool] = useState<{ id: number; name: string } | null>(null);
  const [grade, setGrade] = useState('');
  const [cityOptions, setCityOptions] = useState<SelectOption[]>([]);
  const [gradeOptions, setGradeOptions] = useState<string[]>([]);
  const [schoolOptions, setSchoolOptions] = useState<SelectOption[]>([]);
  const [schoolQuery, setSchoolQuery] = useState('');
  const [schoolLoading, setSchoolLoading] = useState(false);
  const [schoolTruncated, setSchoolTruncated] = useState(false);
  useEffect(() => {
    authApi.publicGrades().then((res) => {
      setGradeOptions((res.data ?? []).map((g) => g.name));
    }).catch(() => setGradeOptions([]));
    authApi.publicCities().then((res) => {
      setCityOptions(res.data ?? []);
    }).catch(() => setCityOptions([]));
  }, []);
  // Okul listesi: seçili şehre göre, 300ms debounce'lu sunucu araması.
  // limit=51 istenir; 51 geldiyse liste kesilmiştir → footer notu gösterilir.
  useEffect(() => {
    if (!city) {
      setSchoolOptions([]);
      setSchoolTruncated(false);
      return;
    }
    setSchoolLoading(true);
    const t = setTimeout(() => {
      authApi.publicInstitutions({ cityId: city.id, q: schoolQuery.trim() || undefined, limit: 51 })
        .then((res) => {
          const rows = res.data ?? [];
          setSchoolTruncated(rows.length > 50);
          setSchoolOptions(rows.slice(0, 50));
        })
        .catch(() => {
          setSchoolOptions([]);
          setSchoolTruncated(false);
        })
        .finally(() => setSchoolLoading(false));
    }, schoolQuery ? 300 : 0);
    return () => clearTimeout(t);
  }, [city, schoolQuery]);

  // Step 4 — AP35: profil fotoğrafı artık gerçekten seçilebiliyor; dosya kayıt
  // tamamlanınca (register token döndürür) /auth/me/avatar'a yüklenir.
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoError, setPhotoError] = useState('');
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!photoFile) {
      setPhotoPreview(null);
      return;
    }
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);
  const onPhotoPick = (f: File | null) => {
    setPhotoError('');
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
      setPhotoError('Yalnızca JPEG, PNG veya WebP formatında görsel yükleyebilirsiniz');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setPhotoError('Fotoğraf 5MB sınırını aşıyor');
      return;
    }
    setPhotoFile(f);
  };

  // Step 5
  const [regPassword, setRegPassword] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');

  // Validate the step the user is ON, before letting them leave it.
  //
  // Every rule below mirrors registerSchema in birey-backend auth.routes.ts. The
  // backend was already rejecting all of this correctly — the problem was purely
  // that the wizard let you walk from step 1 to step 5 with every field blank (or
  // a first name of "12345 !@#$%^&*()"), and the first thing you heard was a
  // single backend message on the final submit, naming one field with no clue
  // which of the five steps it lived on. Catching it here puts the message next
  // to the box that caused it.
  //
  // Deliberately NOT stricter than the server: anything accepted here must be
  // accepted there, or users hit a wall they cannot read.
  const validateStep = (s: number): string | null => {
    if (s === 1) {
      if (!regEmail.trim()) return 'E-posta adresinizi girin';
      // Same shape zod's .email() accepts: local@domain.tld, no spaces.
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(regEmail.trim())) return 'Geçerli bir e-posta adresi girin';
      // Optional, but if given it must be a real TR mobile (5XXXXXXXXX after the
      // +90 prefix the form already shows).
      const digits = phone.replace(/\D/g, '');
      if (phone.trim() && !/^5\d{9}$/.test(digits)) return 'Geçerli bir cep telefonu girin (örn. 532 123 45 67)';
      if (regType === 'Veli' && !publisherCode.trim()) return "Çocuğunuzun Öğrenci ID'sini girin";
    }
    if (s === 2) {
      if (!firstName.trim()) return 'Adınızı girin';
      if (!lastName.trim()) return 'Soyadınızı girin';
      // Letters (incl. Turkish), space, apostrophe and hyphen — no digits or
      // symbols. Matches isValidPersonName on the server.
      const NAME = /^[\p{L}][\p{L} '’-]{1,}$/u;
      if (!NAME.test(firstName.trim())) return 'Geçerli bir ad girin';
      if (!NAME.test(lastName.trim())) return 'Geçerli bir soyad girin';
      if (firstName.trim().length > 60) return 'Ad en fazla 60 karakter olabilir';
      if (lastName.trim().length > 60) return 'Soyad en fazla 60 karakter olabilir';
      if (birthDate) {
        const d = new Date(birthDate);
        if (Number.isNaN(d.getTime())) return 'Doğum tarihini GG.AA.YYYY biçiminde girin';
        const year = d.getFullYear();
        if (d.getTime() > Date.now()) return 'Doğum tarihi gelecekte olamaz';
        if (year < 1900) return 'Doğum tarihini kontrol edin';
      }
    }
    return null;
  };

  const nextStep = async () => {
    const stepError = validateStep(step);
    if (stepError) {
      setError(stepError);
      return;
    }
    setError('');
    if (step === 5) {
      if (regPassword !== regPasswordConfirm) {
        setError('Şifreler eşleşmiyor');
        return;
      }
      if (regPassword.length < 8) {
        setError('Şifre en az 8 karakter olmalıdır');
        return;
      }
      // A password of nothing but spaces clears min(8) on both sides — the server
      // accepted "        " during testing. Require actual characters.
      if (!regPassword.trim()) {
        setError('Şifre yalnızca boşluk olamaz');
        return;
      }
      setError('');
      const result = await register({
        name: firstName,
        surname: lastName,
        email: regEmail,
        password: regPassword,
        role: ROLE_MAP[regType] ?? 'student',
        institutionCode: publisherCode || undefined,
        phone: phone ? `+90${phone}` : undefined,
        // AP59: seçilen okul + doğum tarihi artık kayıtla birlikte gidiyor.
        institutionId: school?.id,
        birthDate: birthDate || undefined,
      });
      if (!result.ok) {
        // Backend'in gerçek mesajını göster (ör. "Bu telefon numarası başka
        // bir hesaba kayıtlı") — jenerik metin tanıyı imkânsız kılıyordu.
        setError(result.error || 'Kayıt oluşturulamadı. Lütfen bilgilerinizi kontrol edin.');
        return;
      }
      // AP35: seçilen profil fotoğrafını kayıt sonrası yükle — hata kaydı bloklamaz.
      if (photoFile) {
        try {
          await authApi.uploadAvatar(photoFile);
        } catch {
          /* fotoğraf yüklenemese de kayıt başarılı — profilden tekrar denenebilir */
        }
      }
    }
    goToStep(Math.min(step + 1, 6));
  };
  const prevStep = () => navigate(-1);

  // ---- Step 6: Success (blue background, Figma layout) ----
  if (step === 6) {
    return (
      <div className="min-h-screen bg-[#3973bf] relative overflow-hidden">
        {/* Tiling background */}
        <div className="absolute inset-0" style={{ backgroundImage: "url('/a-tilingback.png')", backgroundSize: 'cover', backgroundPosition: 'center', mixBlendMode: 'color-burn', opacity: 0.7 }} />

        {/* Logo — top center */}
        <div className="relative z-10 flex justify-center pt-[141px] mb-[100px]">
          <BrandLogo variant="full" className="h-[72px] object-contain" />
        </div>

        <div className="relative z-10 flex px-[120px]">
          {/* Left: text content */}
          <div className="flex-1 max-w-[1158px]">
            <h1 style={{ fontFamily: 'Inter', fontSize: 32, fontWeight: 600, color: '#fafafa', marginBottom: 24 }}>
              bryekosis LMS'e Hoş Geldiniz!
            </h1>
            <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 500, color: '#fafafa', lineHeight: '1.6', marginBottom: 24 }}>
              bryekosis LMS uygulaması, öğrenme sürecinizi daha etkili, keyifli ve verimli hale getirmek için tasarlandı.
              Ders içeriklerini kendi hızınızda keşfedebilir, ilerlemenizi takip edebilir ve bilgilerinizi adım adım geliştirebilirsiniz.
              Öğrenmeye başlamak için hazırsanız, şimdi keşfetme zamanı!
            </p>
            <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#fafafa', marginBottom: 48 }}>
              Kaydınız başarıyla oluşturuldu. Devam etmek için lütfen tekrar giriş yapın.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="rounded-[20px] hover:opacity-90 transition-opacity"
              style={{ background: '#e5599b', width: 631, height: 63 }}
            >
              <span style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#fafafa' }}>Ana Sayfaya Dön</span>
            </button>
          </div>

          {/* Right: mascot in pink circle */}
          <div className="flex-shrink-0 ml-12">
            <div
              className="w-[329px] h-[329px] rounded-full overflow-hidden flex items-center justify-center"
              style={{ background: '#e5599b' }}
            >
              <img src="/a-register-success-mascot.png" alt="Mascot" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Publisher code info popup — opens on all screen sizes when the
          "Yayınevi kodu nedir?" link in the left form panel is clicked. On lg
          screens the right panel ALSO shows the answer as the mascot speech
          bubbles (the child asks "Yayınevi kodu nedir?", the adult replies);
          both the bubbles and this click-to-open popup coexist. */}
      {showCodeInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-60" onClick={() => setShowCodeInfo(false)} />
          <div
            className="relative bg-white rounded-[24px] w-full max-w-[600px] p-8"
            style={{ boxShadow: '0px 4px 16px rgba(0,0,0,0.2)' }}
          >
            <button
              onClick={() => setShowCodeInfo(false)}
              className="absolute top-4 right-4 w-[36px] h-[36px] rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round">
                <line x1="3" y1="3" x2="13" y2="13" /><line x1="13" y1="3" x2="3" y2="13" />
              </svg>
            </button>

            <h2 style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 700, color: '#3973bf', marginBottom: 20 }}>
              Yayınevi Kitap Kodu Nedir?
            </h2>

            <div className="space-y-5">
              {/* A-1: the two student-kind tooltip options. Copy used to say
                  "Anlaşmalı Okul Öğrencisiyim" (partnered-school student)
                  which the product asked to retire. Renamed to
                  "Kurumsal Öğrenci" / "Bireysel Öğrenci" to match the
                  institutional / individual student framing. */}
              {/* Option 1 — institutional student */}
              <div className="bg-[#fafafa] rounded-[16px] p-5" style={{ border: '2px solid #3973bf' }}>
                <div className="flex items-center gap-3 mb-2">
                  <SchoolIcon />
                  <p style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 600, color: '#3973bf' }}>Kurumsal Öğrenci</p>
                </div>
                <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#2b2b2b', lineHeight: '1.6' }}>
                  Kurumsal öğrenciler için hesaplar kurum tarafından oluşturulmaktadır.
                  Yayınevi kitap kodunuzu almak için lütfen okulunuzun rehberlik veya bilgi işlem birimiyle iletişime geçiniz.
                </p>
              </div>

              {/* Option 2 — individual student */}
              <div className="bg-[#fafafa] rounded-[16px] p-5" style={{ border: '2px solid #e5599b' }}>
                <div className="flex items-center gap-3 mb-2">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="#e5599b"><path d="M18 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V4a2 2 0 00-2-2zM6 4h5v8l-2.5-1.5L6 12V4z" /></svg>
                  <p style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 600, color: '#e5599b' }}>Bireysel Öğrenci</p>
                </div>
                <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#2b2b2b', lineHeight: '1.6' }}>
                  Bireysel olarak kaydolan öğrenciler için yayınevi kitap kodu, bryekosis LMS erişimli kitaplarımızın iç kapağında veya arka sayfasında yer almaktadır.
                  Bu kodu ilgili alana girerek kaydınızı tamamlayabilirsiniz.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Left - White side. Percentage width (matching Login's lg:w-[45.5%])
          so both panels shrink together as the viewport narrows, instead of a
          fixed 874px that pins the left panel while only the right shrinks. */}
      <div className="w-full lg:w-[45.5%] min-h-screen bg-[#fafafa] flex flex-col px-6 lg:px-[60px] py-8">
        {/* AP33: üstteki Geri oku artık adım-duyarlı — 2+. adımda bir önceki
            adıma döner (eskiden her adımda login'e fırlatıyordu), 1. adımda
            giriş ekranına çıkar. Adım URL'de olduğundan tarayıcı geri tuşu da
            aynı davranışı gösterir. */}
        <button
          type="button"
          onClick={() => (step > 1 ? navigate(-1) : navigate('/login'))}
          className="self-start flex items-center gap-2 mb-2 text-[#3973bf] hover:underline"
          style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, background: 'transparent', border: 'none', cursor: 'pointer' }}
          aria-label={step > 1 ? 'Önceki adıma dön' : 'Giriş ekranına dön'}
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="#3973bf" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M13 5l-6 5 6 5" />
          </svg>
          Geri
        </button>

        <StepBar currentStep={step} />

        <h1 className="mt-6 mb-5" style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600, color: '#2b2b2b', textAlign: 'center' }}>
          Kayıt Ol
        </h1>

        <div className="flex-1 px-0 lg:px-[55px]">
          {step === 1 && (
            <>
              <FormSelect label="Kayıt Türü Seçiniz" placeholder="Kayıt Türü Seçiniz" value={regType}
                options={[{ id: 'Öğrenci', name: 'Öğrenci' }, { id: 'Öğretmen', name: 'Öğretmen' }, { id: 'Veli', name: 'Veli' }]}
                onSelect={(o) => setRegType(o.name)} icon={<PersonIcon />} />
              {regType === 'Veli' ? (
                <FormInput label="Öğrenci ID" value={publisherCode} onChange={setPublisherCode}
                  placeholder="Çocuğunuzun Öğrenci ID'sini Giriniz" icon={<KeyIcon />} />
              ) : (
                <div className="relative">
                  <FormInput label="Yayınevi Kodu" value={publisherCode} onChange={setPublisherCode}
                    placeholder="Yayınevi Kodunu Giriniz" icon={<KeyIcon />} />
                  {/* Sarı banner kaldırıldığından (#4) yayınevi kodu için bilgi
                      ⓘ ikonu burada popup'ı açıyor. */}
                  <button
                    type="button"
                    onClick={() => setShowCodeInfo(true)}
                    aria-label="Yayınevi koduna nasıl ulaşırım?"
                    className="absolute right-0 top-0 mt-1 text-[12px] font-semibold text-[#3973bf] hover:underline flex items-center gap-1"
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#3973bf" strokeWidth="1.6">
                      <circle cx="8" cy="8" r="6.6" />
                      <circle cx="8" cy="5" r="0.6" fill="#3973bf" />
                      <path d="M8 8v3" strokeLinecap="round" />
                    </svg>
                    Yayınevi kodu nedir?
                  </button>
                </div>
              )}
              <FormInput label="E-Posta" value={regEmail} onChange={setRegEmail}
                placeholder="E-Posta Adresinizi Giriniz" type="email" icon={<MailIcon />} />
              {/* Digits only, capped at 10 (5XXXXXXXXX after the +90 prefix).
                  Typing letters used to be possible all the way to submit. */}
              <FormInput label="Telefon Numarası" value={phone}
                onChange={(v) => setPhone(v.replace(/\D/g, '').slice(0, 10))}
                placeholder="5XX XXX XX XX" prefix="+90" inputMode="numeric" icon={<PhoneIcon />} />
            </>
          )}

          {step === 2 && (
            <>
              <FormInput label="Ad" value={firstName} onChange={setFirstName}
                placeholder="Adınızı Giriniz" maxLength={60} icon={<PersonIcon />} />
              <FormInput label="Soyad" value={lastName} onChange={setLastName}
                placeholder="Soyadınızı Giriniz" maxLength={60} icon={<PersonIcon />} />
              {/* max=today: the picker used to offer 2099, which the server then
                  rejected five steps later. */}
              <FormInput label="Doğum Tarihi (Gün / Ay / Yıl)" value={birthDate} onChange={setBirthDate}
                placeholder="Doğum Tarihi Giriniz" type="date" max={new Date().toISOString().slice(0, 10)}
                icon={<CalendarIcon />} />
            </>
          )}

          {step === 3 && (
            <>
              <FormSelect label="Şehir" placeholder="Şehir Seçiniz" value={city?.name ?? null}
                options={cityOptions} searchable
                onSelect={(o) => {
                  setCity({ id: Number(o.id), name: o.name });
                  // Şehir değişince okul seçimi ve araması sıfırlanır (AP34 senkron).
                  setSchool(null);
                  setSchoolQuery('');
                }}
                icon={<LocationIcon />} />
              <FormSelect label="Okul Seçiniz" placeholder="Okul Seçiniz" value={school?.name ?? null}
                options={schoolOptions} searchable
                searchValue={schoolQuery} onSearchChange={setSchoolQuery}
                loading={schoolLoading}
                disabled={!city} disabledText="Önce şehir seçiniz"
                footerNote={schoolTruncated ? 'İlk 50 sonuç gösteriliyor — aramayı daraltın' : null}
                emptyText="Bu aramayla eşleşen okul bulunamadı"
                onSelect={(o) => setSchool({ id: Number(o.id), name: o.name })}
                icon={<SchoolIcon />} />
              <FormSelect label="Sınıf Seçiniz" placeholder="Sınıf Seçiniz" value={grade || null}
                options={gradeOptions.map((g) => ({ id: g, name: g }))}
                onSelect={(o) => setGrade(o.name)} icon={<SchoolIcon />} />
            </>
          )}

          {step === 4 && (
            <div className="flex flex-col items-center pt-8">
              {/* AP35: gerçek dosya seçici — daire tıklanınca resim seçilir,
                  önizleme dairede gösterilir; yükleme kayıt tamamlanınca yapılır. */}
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  onPhotoPick(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="w-[241px] h-[241px] rounded-full flex items-center justify-center mb-6 overflow-hidden hover:opacity-90 transition-opacity"
                style={{ background: '#e5599b' }}
                aria-label={photoFile ? 'Profil fotoğrafını değiştir' : 'Profil fotoğrafı seç'}
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="Profil fotoğrafı önizleme" className="w-full h-full object-cover" />
                ) : (
                  <svg width="69" height="69" viewBox="0 0 24 24" fill="#fafafa">
                    <path d="M20 4h-3.17L15 2H9L7.17 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm-8 13a5 5 0 110-10 5 5 0 010 10z" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="hover:underline"
                style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#3973bf', marginBottom: 8, background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                {photoFile ? 'Fotoğrafı Değiştir' : 'Profil resmi yükle'}
              </button>
              <p style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 500, color: '#8d8d8d', marginBottom: 16 }}>
                {photoFile ? photoFile.name : 'İsteğe bağlı — bu adımı atlayabilirsiniz'}
              </p>
              {photoError && (
                <p style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#e5484d', marginBottom: 16 }}>{photoError}</p>
              )}
              <div className="bg-white rounded-[20px] p-6 w-full max-w-[631px]"
                style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 500, color: '#2b2b2b', lineHeight: '1.6' }}>
                <p>Profil resmi kuralları:</p>
                <p>- Yüzünüz net görünmelidir</p>
                <p>- Uygunsuz içerik kabul edilmez</p>
                <p>- Maksimum 5MB boyutunda olmalıdır</p>
              </div>
            </div>
          )}

          {step === 5 && (
            <>
              <FormInput label="Şifre" value={regPassword} onChange={setRegPassword}
                placeholder="Şifrenizi Giriniz" type="password" icon={<KeyIcon />} />
              <FormInput label="Şifre (Tekrar)" value={regPasswordConfirm} onChange={setRegPasswordConfirm}
                placeholder="Şifrenizi Tekrar Giriniz" type="password" icon={<KeyIcon />} />
              <div className="bg-white rounded-[20px] p-6"
                style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 500, color: '#3d3d3d', lineHeight: '1.7' }}>
                <p>Hesap güvenliğinizi sağlamak amacıyla oluşturacağınız şifrenin aşağıdaki kurallara uygun olması gerekmektedir:</p>
                <p className="mt-2">Şifreniz en az 8 karakter uzunluğunda olmalıdır.</p>
                <p>En az bir büyük harf (A-Z) içermelidir.</p>
                <p>En az bir küçük harf (a-z) içermelidir.</p>
                <p>En az bir rakam (0-9) içermelidir.</p>
                <p>En az bir özel karakter içermelidir (! @ # $ % ^ & * gibi).</p>
                <p className="mt-2">Şifreniz adınız, soyadınız, doğum tarihiniz gibi kolay tahmin edilebilir bilgiler içermemelidir.</p>
                <p>Güvenliğiniz için şifrenizi kimseyle paylaşmamanız önerilir.</p>
              </div>
            </>
          )}
        </div>

        {/* Navigation buttons */}
        {error && (
          <p className="px-0 lg:px-[55px] text-red-500 text-sm text-center mb-2">{error}</p>
        )}
        {/* KVKK m.10: aydınlatma, veri işleme BAŞLAMADAN yapılır — kayıt formu
            gönderilmeden önce metnin görünür olması gerekiyor. Bu bir rıza
            kutusu değil, bilgilendirme; aydınlatma rızaya bağlı değildir. */}
        {step === 5 && (
          <p className="px-0 lg:px-[55px] pb-3 text-center text-[12.5px] leading-[1.6] text-[#5b6472]">
            Kaydınızı tamamlayarak kişisel verilerinizin{' '}
            <Link to="/aydinlatma-metni" className="font-semibold text-[#3973bf] hover:underline">
              Aydınlatma Metni
            </Link>{' '}
            kapsamında işleneceği hakkında bilgilendirildiğinizi kabul edersiniz.
          </p>
        )}
        <div className="px-0 lg:px-[55px] pb-4 flex gap-3">
          {step > 1 && (
            <button onClick={prevStep}
              className="rounded-[20px] h-[63px] px-8 bg-white border-2 border-[#3973bf] hover:bg-blue-50 transition-colors">
              <span style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#3973bf' }}>Geri</span>
            </button>
          )}
          <button onClick={nextStep}
            className="rounded-[20px] h-[63px] flex-1 hover:opacity-90 transition-opacity"
            style={{ background: '#3973bf' }}>
            <span style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#fafafa' }}>
              {step === 5 ? 'Kaydınızı Tamamlayın' : 'İleri'}
            </span>
          </button>
        </div>

        <div className="px-0 lg:px-[55px] pb-6">
          <LegalLinks />
        </div>
      </div>

      {/* Right - Blue side */}
      <div className="hidden lg:flex flex-1 bg-[#3973bf] relative overflow-hidden flex-col">
        {/* Tiling background */}
        <div className="absolute inset-0" style={{ backgroundImage: "url('/a-tilingback.png')", backgroundSize: 'cover', backgroundPosition: 'center', mixBlendMode: 'color-burn', opacity: 0.7 }} />

        {/* Info text — hidden on the Yayınevi Kodu step so the adult's answer
            speech bubble has room to sit above the adult's head. */}
        {!(step === 1 && regType !== 'Veli') && (
          <div className="relative z-10 px-8 mt-8 mb-4">
            <p style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 700, color: '#fafafa', lineHeight: '1.5' }}>
              Dijital eğitim platformu ile öğrenmenin keyfini çıkarın.
            </p>
          </div>
        )}

        {/* Mascot + conversation. The two figures are the ADULT (left) and the
            CHILD (right). On the Yayınevi Kodu step the child asks the question
            in a speech bubble and the adult answers with the code-info content
            (same copy as the small-screen popup) — both shown by default, no
            click needed. Bubble positions are tuned visually against
            a-register-mascot.png; image is bottom-aligned to leave headroom. */}
        <div className={`relative z-10 flex-1 flex items-start justify-center px-6 ${step === 1 && regType !== 'Veli' ? 'pt-[285px]' : ''}`}>
          <div className="relative">
            {step === 1 && regType !== 'Veli' && (
              <>
                {/* Adult's answer bubble — anchored just above the adult's head
                    (left figure). `bottom-full` lifts the bubble so its tail sits
                    above the head instead of covering the figure. Always visible. */}
                <div className="absolute z-30 top-[4px] left-[6px]">
                  {/* Widened ~20% (320→384) with ~20% larger type so the copy
                      grows horizontally instead of getting taller. */}
                  <div className="absolute bottom-full mb-3 w-[384px]">
                    <div className="relative bg-white rounded-[18px] p-2.5" style={{ boxShadow: '0px 6px 20px rgba(0,0,0,0.25)' }}>
                      <div className="space-y-1.5">
                        {/* Option 1 — institutional student */}
                        <div className="bg-[#fafafa] rounded-[12px] p-2" style={{ border: '2px solid #3973bf' }}>
                          <p style={{ fontFamily: 'Inter', fontSize: 14.4, fontWeight: 600, color: '#3973bf', marginBottom: 2 }}>Kurumsal Öğrenci</p>
                          <p style={{ fontFamily: 'Inter', fontSize: 12.6, fontWeight: 500, color: '#2b2b2b', lineHeight: '1.35' }}>
                            Kurumsal öğrenciler için hesaplar kurum tarafından oluşturulmaktadır.
                            Yayınevi kitap kodunuzu almak için okulunuzun rehberlik veya bilgi işlem birimiyle iletişime geçiniz.
                          </p>
                        </div>

                        {/* Option 2 — individual student */}
                        <div className="bg-[#fafafa] rounded-[12px] p-2" style={{ border: '2px solid #e5599b' }}>
                          <p style={{ fontFamily: 'Inter', fontSize: 14.4, fontWeight: 600, color: '#e5599b', marginBottom: 2 }}>Bireysel Öğrenci</p>
                          <p style={{ fontFamily: 'Inter', fontSize: 12.6, fontWeight: 500, color: '#2b2b2b', lineHeight: '1.35' }}>
                            Bireysel kaydolan öğrenciler için yayınevi kitap kodu, bryekosis LMS erişimli kitaplarımızın iç kapağında veya arka sayfasında yer alır.
                            Bu kodu ilgili alana girerek kaydınızı tamamlayabilirsiniz.
                          </p>
                        </div>
                      </div>

                      {/* tail pointing down toward the adult */}
                      <div className="absolute -bottom-[7px] left-[124px] w-[16px] h-[16px] bg-white rotate-45" />
                    </div>
                  </div>
                </div>

                {/* Child's question bubble — anchored just above the child's head
                    (right figure). */}
                <div className="absolute z-30 top-[84px] right-[10px]">
                  <div className="absolute bottom-full mb-3 right-0 w-[200px]">
                    <div className="relative bg-white rounded-[18px] px-4 py-3" style={{ boxShadow: '0px 4px 14px rgba(0,0,0,0.20)' }}>
                      <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#3973bf' }}>
                        Yayınevi kodu nedir?
                      </p>
                      {/* tail pointing down toward the child */}
                      <div className="absolute -bottom-[7px] right-[70px] w-[16px] h-[16px] bg-white rotate-45" />
                    </div>
                  </div>
                </div>
              </>
            )}

            <img src="/a-register-mascot.png" alt="Mascot" className="w-[520px] h-auto object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
